import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faGift,
  faSpinner,
  faTrophy,
  faCheck,
  faClock,
  faTimes,
  faChevronDown,
  faChevronUp,
} from "@fortawesome/free-solid-svg-icons";
import { Collapse } from "reactstrap";
import AccountSidebar from "../../components/AccountSidebar/AccountSidebar";
import { getPrizeOrdersApi } from "../../services/api.services";
import { language } from "../../languageProvider/language/language";
import { defaultLanguage } from "../../helper/utility";
import "../Profile.page.css";

const getOrderStatus = (order) => {
  if (order.cancelled_at) return { label: "Annullato", color: "#dc3545", icon: faTimes };
  if (order.fulfilled_at) return { label: "Consegnato", color: "#28a745", icon: faCheck };
  if (order.confirmed_at) return { label: "Confermato", color: "#17a2b8", icon: faClock };
  return { label: "In Elaborazione", color: "#ffc107", icon: faClock };
};

const TAB_FILTERS = {
  tutti: () => true,
  in_corso: (o) => !o.cancelled_at && !o.fulfilled_at,
  completati: (o) => !!o.fulfilled_at || !!o.cancelled_at,
};

function PrizesPage(props) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("tutti");
  const [expandedOrder, setExpandedOrder] = useState(null);

  const user = props.authJWT?.user;

  const fetchPrizeOrders = useCallback(async () => {
    if (!user?.id && !user?.apiUserId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      let allOrders = [];
      let page = 1;
      let totalPages = 1;

      while (page <= totalPages) {
        const res = await getPrizeOrdersApi(user.apiUserId || user.id, page, 20);
        if (res.status === 200 && res.data) {
          allOrders = [...allOrders, ...(res.data.orders || [])];
          totalPages = res.data.pagination?.total_pages || 1;
          page++;
        } else {
          setError(res.message || "Errore nel caricamento dei premi");
          break;
        }
      }

      allOrders.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setOrders(allOrders);
    } catch (err) {
      setError("Errore nel caricamento dei premi");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchPrizeOrders();
  }, [fetchPrizeOrders]);

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      return new Date(dateStr).toLocaleDateString("it-IT", {
        day: "2-digit", month: "2-digit", year: "numeric",
      });
    } catch { return dateStr; }
  };

  const filteredOrders = orders.filter(TAB_FILTERS[activeTab]);

  // Stats
  const totalOrders = orders.length;
  const inCorso = orders.filter(TAB_FILTERS.in_corso).length;
  const completati = orders.filter((o) => !!o.fulfilled_at).length;
  const totalPoints = orders.reduce((sum, o) => sum + (o.total_points || 0), 0);

  return (
    <div className="up-page">
      <div className="up-topbar">
        <div className="up-topbar__left">
          <div className="up-topbar__back" onClick={() => navigate("/leaflet")}>
            <FontAwesomeIcon icon={faArrowLeft} />
          </div>
          <h1 className="up-topbar__title">{language[defaultLanguage].premi}</h1>
        </div>
      </div>

      <div className="up-layout">
        <AccountSidebar />
        <div className="up-main">
          {loading && (
            <div className="up-card" style={{ textAlign: "center", padding: "40px" }}>
              <FontAwesomeIcon icon={faSpinner} spin style={{ fontSize: "1.5rem", color: "#999" }} />
            </div>
          )}

          {error && (
            <div className="up-card" style={{ textAlign: "center", color: "#dc3545" }}>
              <p>{error}</p>
              <button className="up-btn up-btn--outline" onClick={fetchPrizeOrders}>Riprova</button>
            </div>
          )}

          {!loading && !error && (
            <>
              {/* Stats */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: "12px" }}>
                <div className="up-card" style={{ textAlign: "center", padding: "14px" }}>
                  <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#222" }}>{totalOrders}</div>
                  <div style={{ fontSize: "0.78rem", color: "#999" }}>Totale ordini</div>
                </div>
                <div className="up-card" style={{ textAlign: "center", padding: "14px" }}>
                  <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#ffc107" }}>{inCorso}</div>
                  <div style={{ fontSize: "0.78rem", color: "#999" }}>In corso</div>
                </div>
                <div className="up-card" style={{ textAlign: "center", padding: "14px" }}>
                  <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "#28a745" }}>{completati}</div>
                  <div style={{ fontSize: "0.78rem", color: "#999" }}>Completati</div>
                </div>
                <div className="up-card" style={{ textAlign: "center", padding: "14px" }}>
                  <div style={{ fontSize: "1.4rem", fontWeight: 800, color: "var(--primary-color)" }}>{totalPoints.toLocaleString()}</div>
                  <div style={{ fontSize: "0.78rem", color: "#999" }}>Punti usati</div>
                </div>
              </div>

              {/* Tabs */}
              <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                {[
                  { key: "tutti", label: "Tutti" },
                  { key: "in_corso", label: "In corso" },
                  { key: "completati", label: "Completati" },
                ].map((tab) => (
                  <button
                    key={tab.key}
                    className={`up-btn ${activeTab === tab.key ? "up-btn--primary" : "up-btn--outline"}`}
                    style={{ fontSize: "0.82rem", padding: "6px 16px" }}
                    onClick={() => setActiveTab(tab.key)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Orders list */}
              {filteredOrders.length === 0 ? (
                <div className="up-card" style={{ textAlign: "center", padding: "40px", color: "#999" }}>
                  <FontAwesomeIcon icon={faTrophy} style={{ fontSize: "2rem", marginBottom: "10px", color: "#ddd" }} />
                  <p>Nessun premio trovato</p>
                </div>
              ) : (
                filteredOrders.map((order) => {
                  const status = getOrderStatus(order);
                  const isExpanded = expandedOrder === order.id;
                  return (
                    <div className="up-card" key={order.id} style={{ padding: 0, overflow: "hidden" }}>
                      {/* Header */}
                      <div
                        onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "16px 20px",
                          cursor: "pointer",
                        }}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <FontAwesomeIcon icon={faGift} style={{ color: "#888" }} />
                          <div>
                            <div style={{ fontSize: "0.95rem", fontWeight: 700, color: "#222" }}>
                              Ordine #{order.code}
                            </div>
                            <div style={{ fontSize: "0.8rem", color: "#999" }}>
                              {formatDate(order.created_at)}
                            </div>
                          </div>
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <div style={{ textAlign: "right" }}>
                            <span style={{
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              color: status.color,
                              backgroundColor: status.color + "18",
                              padding: "3px 10px",
                              borderRadius: "12px",
                              display: "inline-block",
                            }}>
                              <FontAwesomeIcon icon={status.icon} style={{ marginRight: "4px", fontSize: "0.65rem" }} />
                              {status.label}
                            </span>
                            <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "#222", marginTop: "3px" }}>
                              {order.total_points.toLocaleString()} punti
                            </div>
                          </div>
                          <FontAwesomeIcon
                            icon={isExpanded ? faChevronUp : faChevronDown}
                            style={{ color: "#ccc", fontSize: "0.75rem" }}
                          />
                        </div>
                      </div>

                      {/* Expanded content */}
                      <Collapse isOpen={isExpanded}>
                        <div style={{ padding: "0 20px 16px", borderTop: "1px solid #f0f0f0" }}>
                          {/* Items */}
                          {order.items && order.items.length > 0 && (
                            <div style={{ display: "flex", flexDirection: "column", gap: "8px", marginTop: "12px" }}>
                              {order.items.map((item, idx) => {
                                const mainImg = item.immagine?.find((i) => i.main === 1) || item.immagine?.[0];
                                return (
                                  <div key={idx} style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "10px",
                                    padding: "8px 10px",
                                    borderRadius: "8px",
                                    border: "1px solid #f0f0f0",
                                  }}>
                                    <div style={{
                                      width: "44px",
                                      height: "44px",
                                      minWidth: "44px",
                                      borderRadius: "8px",
                                      backgroundColor: "#f5f5f5",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      overflow: "hidden",
                                    }}>
                                      {mainImg?.url ? (
                                        <img src={mainImg.url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                      ) : (
                                        <FontAwesomeIcon icon={faGift} style={{ color: "#ccc" }} />
                                      )}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                      <div style={{ fontSize: "0.88rem", fontWeight: 600, color: "#222" }}>{item.nome}</div>
                                      <div style={{ fontSize: "0.78rem", color: "#888" }}>
                                        Qtà: {item.qty} &middot; {item.total_points.toLocaleString()} punti
                                        {parseFloat(item.total_cash) > 0 && ` + € ${parseFloat(item.total_cash).toFixed(2)}`}
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}

                          {/* Extra info */}
                          <div style={{ marginTop: "12px", fontSize: "0.82rem", color: "#888", display: "flex", flexDirection: "column", gap: "4px" }}>
                            {parseFloat(order.total_cash) > 0 && (
                              <div>Contributo: <strong>€ {parseFloat(order.total_cash).toFixed(2)}</strong></div>
                            )}
                            {order.fulfilled_at && (
                              <div>Consegnato il: <strong>{formatDate(order.fulfilled_at)}</strong></div>
                            )}
                            {order.note && (
                              <div>Note: {order.note}</div>
                            )}
                          </div>
                        </div>
                      </Collapse>
                    </div>
                  );
                })
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => ({
  authJWT: state.authJWT,
});

export default connect(mapStateToProps)(PrizesPage);
